import { encodeToUriSafeBase64String } from "../../../core/helpers/uri-util.js";
import Model from "../../Model.js";

/**
 * `periodReportAttachment` is an attached file to a period report.
 *
 * @param {*} spec - Parameter object
 * @param {string} spec.id - identifier of file
 * @param {string} spec.filename - Name of the file (extension included)
 * @param {string} spec.attachmentType - Can either be 'UploadedAttachment' or
 * 'GeneratedAttachment'.
 *
 * - Generated attachments are not used anymore. They were automatically
 * generated on the server (IS and BS PDF files) and still exist in old period
 * reports.
 *
 * - Uploaded attachments are uploaded from the client to the server. All
 * attachments in new period reports fall into this category.
 *
 * Uploaded attachments can either be supplied by the user or be built by the
 * client-side PDF report engine for income statements and balance sheets (these
 * are default attachments, see the parameter `isDefaultAttachment`).
 * @param {boolean} spec.isDefaultAttachment - True if the attachment is part of
 * the default attachment set (Income statement or Balance sheet PDF file in
 * newly created period reports). These attachments are not supplied by the
 * user, but are added by default to all new period reports.
 */
export default class PeriodReportAttachment extends Model {
	constructor({ id, filename, attachmentType, isDefaultAttachment = false }) {
		super(...arguments);
		this._id = id;
		this._filename = filename;

		this._attachmentType =
			attachmentType || this.constructor.getUploadedAttachmentType();

		this._isDefaultAttachment = isDefaultAttachment;
	}

	getId() {
		return this._id;
	}

	getFilename() {
		return this._filename;
	}

	getEncodedFilename() {
		return encodeToUriSafeBase64String(this._filename);
	}

	getContentPath() {
		return `/Shared/AttachmentContent/${this.getId()}`;
	}

	getFileExtension() {
		const splitted = this._filename.split(".");
		return splitted.length > 1 ? splitted.pop().toLowerCase() : "";
	}

	isUploadedAttachment() {
		return (
			this._attachmentType ===
			this.constructor.getUploadedAttachmentType()
		);
	}

	isGeneratedAttachment() {
		return !this.isUploadedAttachment();
	}

	isDefaultAttachment() {
		return this._isDefaultAttachment;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;
		properties.fileName = this._filename;
		properties.attachmentType = this._attachmentType;
		properties.isDefaultAttachment = this._isDefaultAttachment;

		return properties;
	}

	static transformMaterializationData(data) {
		data.filename = data.fileName;
	}

	static getUploadedAttachmentType() {
		return "UploadedAttachment";
	}

	static getGeneratedAttachmentType() {
		return "GeneratedAttachment";
	}
}
